/**
 * Authentication context.
 *
 * Wraps the app in an `AuthProvider` that tracks the Firebase user via
 * `onAuthStateChanged` and exposes a stable `useAuth()` hook. Two distinct
 * Microsoft flows live here:
 *
 * 1. `signInWithMicrosoft` — app login (Firebase Auth Microsoft SSO popup). This
 *    is only the identity/login layer; it does NOT persist a refresh token.
 * 2. `connectMicrosoft` — the "Connect Microsoft 365" backend OAuth flow that
 *    obtains a 90-day refresh token server-side. It calls the
 *    `getMicrosoftAuthUrl` callable and redirects the browser to Microsoft.
 *
 * The `allowed` custom claim (set by the backend blocking function) is read from
 * the ID token result and surfaced as `claims.allowed`.
 */
import {
  signOut as firebaseSignOut,
  OAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  type User,
} from 'firebase/auth'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { getMicrosoftAuthUrl, refreshAdminClaim } from './callables'
import { auth } from './firebase'
import { clearAllOptimisticState } from './optimisticMessages'

/** Custom claims we care about on the client (set by the backend). */
export interface AuthClaims {
  /** True once the user has passed the domain/email allow-list blocking function. */
  allowed: boolean
  /** True quando o usuário é administrador (claim `admin` carimbado no backend). */
  admin: boolean
}

export interface AuthContextValue {
  /** The signed-in Firebase user, or `null` when signed out. */
  user: User | null
  /** Custom claims resolved from the ID token, or `null` while signed out. */
  claims: AuthClaims | null
  /** True until the initial auth state has resolved. */
  loading: boolean
  /** Sign in to the app with Microsoft SSO (Firebase Auth popup). */
  signInWithMicrosoft: () => Promise<void>
  /** Sign out of the app. */
  signOut: () => Promise<void>
  /**
   * Begin the backend "Connect Microsoft 365" OAuth flow. Redirects the browser
   * to Microsoft; on success the backend returns to `returnTo` (defaults to
   * `/settings?connected=1`).
   */
  connectMicrosoft: (returnTo?: string) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/** App-login scopes. Delegated Graph scopes for routines are granted separately via `connectMicrosoft`. */
const MS_LOGIN_SCOPES = ['User.Read', 'Calendars.Read']

/**
 * Single-tenant Entra directory. This app is registered only in the PG Advogados
 * tenant, so the sign-in authority must be the tenant id — never 'common'
 * (which would allow any Microsoft org/personal account).
 */
const MS_TENANT_ID = import.meta.env.VITE_MS_TENANT_ID || 'f0af5359-fc14-4291-b629-2ab2d3347c92'

function buildMicrosoftProvider(): OAuthProvider {
  const provider = new OAuthProvider('microsoft.com')
  for (const scope of MS_LOGIN_SCOPES) {
    provider.addScope(scope)
  }
  // Restrict sign-in to the organization tenant and force account selection.
  provider.setCustomParameters({ tenant: MS_TENANT_ID, prompt: 'select_account' })
  return provider
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [claims, setClaims] = useState<AuthClaims | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Rastreado no fechamento (não em estado/ref) — o callback abaixo é a
    // mesma instância reusada a cada evento do onAuthStateChanged.
    let previousUid: string | null = null
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      const nextUid = nextUser?.uid ?? null
      if (nextUid !== previousUid) {
        // Troca de conta na mesma aba (login, logout, ou outro usuário sem
        // reload): o store de turnos otimistas é module-level (sobrevive a
        // remounts de rota de propósito — ver lib/optimisticMessages.ts), e
        // sem isto uma mensagem em voo do usuário anterior vazaria para a
        // sessão seguinte.
        clearAllOptimisticState()
        previousUid = nextUid
      }
      setUser(nextUser)
      if (nextUser) {
        // Read the `allowed` claim. On the FIRST sign-in it is stamped by the
        // onUserCreate trigger just after account creation, so it may be absent
        // from the freshly-minted token — force-refresh a few times to pick it
        // up. On later sign-ins the first read already has it (no delay).
        let allowed = false
        let admin = false
        try {
          const result = await nextUser.getIdTokenResult()
          allowed = result.claims.allowed === true
          admin = result.claims.admin === true
        } catch {
          allowed = false
        }
        for (let attempt = 0; !allowed && attempt < 4; attempt++) {
          await new Promise((resolve) => setTimeout(resolve, 1500))
          try {
            const result = await nextUser.getIdTokenResult(true)
            allowed = result.claims.allowed === true
            admin = result.claims.admin === true
          } catch {
            allowed = false
          }
        }
        setClaims({ allowed, admin })

        // Reconcilia o claim `admin` no backend (best-effort, silencioso). Só
        // vale a pena depois que `allowed` resolveu. Se o backend carimbar um
        // valor novo, força um refresh do token para refletir na UI.
        if (allowed) {
          refreshAdminClaim()
            .then(async ({ data }) => {
              if (data.updated) {
                try {
                  const result = await nextUser.getIdTokenResult(true)
                  setClaims({
                    allowed: result.claims.allowed === true,
                    admin: result.claims.admin === true,
                  })
                } catch {
                  // Ignora: mantém os claims já resolvidos.
                }
              }
            })
            .catch(() => {
              // Silencioso: falha na reconciliação não deve travar a UI.
            })
        }
      } else {
        setClaims(null)
      }
      setLoading(false)
    })
    return unsubscribe
  }, [])

  const signInWithMicrosoft = useCallback(async () => {
    const provider = buildMicrosoftProvider()
    const result = await signInWithPopup(auth, provider)
    // The access token here is short-lived and login-only; the durable refresh
    // token is obtained separately by the backend OAuth flow (connectMicrosoft).
    OAuthProvider.credentialFromResult(result)
  }, [])

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth)
  }, [])

  const connectMicrosoft = useCallback(async (returnTo?: string) => {
    const { data } = await getMicrosoftAuthUrl({ returnTo })
    window.location.assign(data.authUrl)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      claims,
      loading,
      signInWithMicrosoft,
      signOut,
      connectMicrosoft,
    }),
    [user, claims, loading, signInWithMicrosoft, signOut, connectMicrosoft],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/** Access the auth context. Throws if used outside an `AuthProvider`. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
